import { mutate } from "swr";
import { ACCENT_PALETTE } from "~/lib/accentPalette";
import { apiMutate } from "~/utils/apiClient";

const { VITE_CONVERSATIONS_API, VITE_ANALYSIS_AGENT_API } = import.meta.env;
export const AGENTS_API_PREFIX = `${VITE_CONVERSATIONS_API}/agents`;
export const DEFAULT_NEW_AGENT_DISPLAY_NAME = "New Agent";
/** `favoriteItems` sub-key for pinned agents; also the first element of the
 * `usePinnedList` SWR cache key. */
export const AGENT_FAVORITES_KEY = "agent-favorites";

/** Which slice of agents the list endpoint returns. `usable` is everything the
 *  caller can use; `editable` narrows to owner/editor; `all` is the admin
 *  visibility bypass. The UI only offers the views a caller's permissions allow. */
export const AgentListView = {
  USABLE: "usable",
  EDITABLE: "editable",
  ALL: "all",
} as const;
export type AgentListView = (typeof AgentListView)[keyof typeof AgentListView];

/** Path for the agents list at the given `view`. The default (`usable`) is
 *  omitted from the URL so it matches the backend default and shares its cache
 *  key with a param-less call. The nextgen list appends its own paging/search
 *  params (`?limit&offset&q`) onto this base. */
export const agentListPath = (params?: { view?: AgentListView }) =>
  params?.view && params.view !== AgentListView.USABLE
    ? `${AGENTS_API_PREFIX}?view=${params.view}`
    : AGENTS_API_PREFIX;
export const agentsByIdsPath = () => `${AGENTS_API_PREFIX}/by-ids`;

/** Path for a single capped page of ENABLED agents, ordered by `display_name`.
 *  For filter dropdowns that surface live agents only and do not page: DISABLED
 *  drafts (the unnamed "New Agent" rows a pod accumulates) are excluded
 *  server-side via `status=ENABLED`, and the set is capped by `limit`. `view` is
 *  always emitted - the `status`/`limit` params already make this a distinct SWR
 *  key from the walked `agentListPath`. */
export const enabledAgentsPath = ({ view, limit }: { view: AgentListView; limit: number }) =>
  `${AGENTS_API_PREFIX}?view=${view}&status=${AgentStatus.ENABLED}&limit=${limit}`;

/** Revalidate every agent-list cache after a mutation: the string list keys
 *  sharing `AGENTS_API_PREFIX` (bare, `?view=`, and paginated `?limit&offset&q`)
 *  plus the favorites cache (`[AGENT_FAVORITES_KEY, ...ids]`). Excludes per-agent
 *  `/agents/{id}` reads. */
export const mutateAgentList = () =>
  mutate((key) => {
    if (typeof key === "string") {
      return key === AGENTS_API_PREFIX || key.startsWith(`${AGENTS_API_PREFIX}?`);
    }
    return Array.isArray(key) && key[0] === AGENT_FAVORITES_KEY;
  });
export const agentPath = (id: string) => `${AGENTS_API_PREFIX}/${id}`;
export const agentDuplicatePath = (sourceId: string) =>
  `${AGENTS_API_PREFIX}/${sourceId}/duplicate`;
export const toolkitsPath = () => `${VITE_CONVERSATIONS_API}/toolkits`;
/** Catalog of models an agent's orchestrator can be configured to run on. */
export const modelsPath = () => `${VITE_CONVERSATIONS_API}/models`;
export const agentToolkitsPath = (agentId: string) => `${agentPath(agentId)}/toolkits`;
/** Live tools surfaced by a toolkit — served by the agent-runtime (introspected
 * from the toolkit's pydantic-ai toolset, including MCP servers). */
export const toolkitToolsPath = (toolkitId: string) =>
  `${VITE_ANALYSIS_AGENT_API}/toolkits/${toolkitId}/tools`;
/** Network-free global catalog of every tool across every toolkit (local +
 * MCP), keyed by the runtime tool name — served by the agent-runtime. */
export const allToolsPath = () => `${VITE_ANALYSIS_AGENT_API}/tools`;
/** Live tools surfaced by a connector's external MCP server — served by the
 * agent-runtime. */
export const connectorToolsPath = (connectorId: string) =>
  `${VITE_ANALYSIS_AGENT_API}/connectors/${connectorId}/tools`;
export const agentToolkitPath = (agentId: string, toolkitId: string) =>
  `${agentToolkitsPath(agentId)}/${toolkitId}`;
export const runtimesPath = () => `${VITE_ANALYSIS_AGENT_API}/runtimes`;
export const connectorsPath = () => `${VITE_CONVERSATIONS_API}/connectors`;
export const agentConnectorsPath = (agentId: string) => `${agentPath(agentId)}/connectors`;
export const agentConnectorPath = (agentId: string, connectorId: string) =>
  `${agentConnectorsPath(agentId)}/${connectorId}`;
export const agentSharesPath = (agentId: string) => `${agentPath(agentId)}/shares`;
export const agentLeavePath = (agentId: string) => `${agentPath(agentId)}/leave`;

export const AGENT_BRAND_COLORS: string[] = ACCENT_PALETTE.map((color) => color.accent);

export interface Toolkit {
  id: string;
  display_name: string;
  description?: string;
  /** The permission scopes a caller needs to use this toolkit's tools. Empty
   * means the toolkit needs no special scope. Synced from the analysis-agent
   * toolkit registry onto the conversations catalog, so it rides along on
   * `GET /toolkits` and every embedded `AgentToolkit.toolkit`. */
  required_scopes: string[];
}

/** A model an agent's orchestrator can be configured to run on — one entry per
 * item returned by `GET /models`. `name` is the value persisted as `model`. */
export interface SelectableModel {
  name: string;
  display_name: string;
}

/** A single tool surfaced by a toolkit or connector, with its live description
 * and human-readable labels resolved from the toolkit registry. */
export interface ToolInfo {
  name: string;
  display_name: string;
  description?: string;
  toolkit_id?: string;
  toolkit_display_name?: string;
}

/** An entry in the network-free global tool catalog — one per runtime tool name
 * across every toolkit (local + MCP), with its human-readable labels. */
export interface ToolCatalogEntry {
  name: string;
  display_name: string;
  toolkit_id: string;
  toolkit_display_name: string;
}

export interface AgentToolkit {
  toolkit: Toolkit;
  created_at: string;
  created_by: string;
}

/** A sandbox runtime the Code Interpreter toolkit can dispatch code to. */
export interface Runtime {
  id: string;
  name: string;
  description: string;
}

export const ConnectorSource = {
  SYSTEM: "SYSTEM",
  USER: "USER",
} as const;
export type ConnectorSource = (typeof ConnectorSource)[keyof typeof ConnectorSource];

export interface Connector {
  id: string;
  source: ConnectorSource;
  name: string;
  /** Human-readable label for the UI. Nullable; callers fall back to `name`
   * (the wire-safe dotted identifier) when this is absent. */
  display_name: string | null;
  description?: string;
  url: string;
  docs_url: string | null;
}

export interface AgentConnector {
  connector: Connector;
  created_at: string;
  created_by: string | null;
}

export const AgentStatus = {
  DISABLED: "DISABLED",
  ENABLED: "ENABLED",
  DELETED: "DELETED",
} as const;
export type AgentStatus = (typeof AgentStatus)[keyof typeof AgentStatus];

export const AgentScope = {
  SYSTEM: "SYSTEM",
  CUSTOMER: "CUSTOMER",
} as const;
export type AgentScope = (typeof AgentScope)[keyof typeof AgentScope];

/** The caller's own resolved access to an agent, and the level a share confers
 * (owner > editor > user). The wire speaks only this level; clients derive
 * per-action capability from it plus `scope` (see the `useCan*Agent` hooks). */
export const AgentAccessLevel = {
  OWNER: "owner",
  EDITOR: "editor",
  USER: "user",
} as const;
export type AgentAccessLevel = (typeof AgentAccessLevel)[keyof typeof AgentAccessLevel];

/** A user who inherits agent access through a shared user-tag group. */
export interface AgentCollaboratorMember {
  email: string;
  given_name?: string | null;
  family_name?: string | null;
  image_url?: string | null;
}

export interface AgentCollaborator {
  /** Bare prefixed principal: `user:{email}` or `user-tag:{tag}`. */
  principal: string;
  level: AgentAccessLevel;
  /** User enrichment from the listing endpoint; null for group principals. */
  given_name?: string | null;
  family_name?: string | null;
  image_url?: string | null;
  /** Group enrichment from the listing endpoint; absent/empty for users. The
   * share (`POST`) response leaves all enrichment fields unset. */
  display_name?: string | null;
  is_internal?: boolean;
  members?: AgentCollaboratorMember[];
}

export interface AgentCollaboratorList {
  items: AgentCollaborator[];
}

export interface AgentStarterPrompt {
  id: string;
  position: number;
  text: string;
  title: string | null;
  icon: string | null;
  created_by: string | null;
  created_at: string;
}

export interface AgentFeaturedConversation {
  id: string;
  position: number;
  label: string;
  url: string;
  created_by: string | null;
  created_at: string;
}

export interface AgentStarterPromptInput {
  text: string;
  title?: string | null;
  icon?: string | null;
}

export interface Agent {
  id: string;
  name: string;
  display_name: string;
  description: string;
  scope: AgentScope;
  customer_id: string | null;
  persona: string;
  instructions: string | null;
  model: string;
  status: AgentStatus;
  parent_agent_id: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
  starter_prompts: AgentStarterPrompt[];
  featured_conversations: AgentFeaturedConversation[];
  /** The caller's own FGA access level, capped by their `manage:agents` scope. A
   * SYSTEM agent resolves to at least `user` for everyone via its `user:*` grant;
   * null appears only when no role resolves (e.g. an admin seeing an agent through
   * the visibility bypass without a direct grant). Drives per-action gating (edit,
   * delete, duplicate, share) via the `useCan*Agent` hooks, which derive each
   * capability from this level plus `scope` — and, for the `admin:agents` FGA
   * bypass, `useCanAdministerAIAgents`, so an admin still acts even at null. */
  access_level: AgentAccessLevel | null;
  /** User-chosen avatar seed; null until customized. */
  avatar_seed: string | null;
  /** User-chosen avatar color, one of `AGENT_BRAND_COLORS`; null until customized. */
  avatar_color: string | null;
}

export const MAX_STARTER_PROMPTS = 6;
export const STARTER_PROMPT_MAX_LENGTH = 300;
export const STARTER_PROMPT_TITLE_MAX_LENGTH = 200;

export type PartialAgent = Omit<Agent, "persona">;

/** Minimal agent identity for labeling assistant turns and rendering the agent
 * avatar in conversation views. Read-only views (public share + admin) receive
 * only this narrow subset — no author emails, customer, model, or timestamps.
 * A full `PartialAgent` is structurally assignable to it. */
export type ConversationAgentSummary = Pick<
  Agent,
  "id" | "name" | "display_name" | "scope" | "avatar_seed" | "avatar_color"
>;

export interface AgentListMetadata {
  limit: number;
  offset: number;
  total_count: number;
  has_more: boolean;
}

export interface AgentList {
  items: Agent[];
  metadata: AgentListMetadata;
}

export interface UpdateAgentInput {
  display_name?: string;
  description?: string;
  persona?: string;
  instructions?: string;
  model?: string;
  status?: AgentStatus;
  starter_prompts?: AgentStarterPromptInput[];
  avatar_seed?: string | null;
  avatar_color?: string | null;
}

/**
 * Resolve a set of agent ids to their `Agent` records via `POST /agents/by-ids`,
 * scoped to what the caller can view. The response is the viewable, non-DELETED
 * subset in request order, so it may be shorter than `ids` — callers treat a
 * missing id as gone (deleted or access-revoked). Returns `[]` for empty input
 * without a request.
 */
export async function fetchAgentsByIds(ids: string[]): Promise<Agent[]> {
  if (ids.length === 0) {
    return [];
  }
  return apiMutate<Agent[]>(agentsByIdsPath(), "POST", { agent_ids: ids });
}

export async function createAgent(): Promise<Agent> {
  return apiMutate<Agent>(agentListPath(), "POST", {
    display_name: DEFAULT_NEW_AGENT_DISPLAY_NAME,
  });
}

export async function duplicateAgent(sourceId: string): Promise<Agent> {
  return apiMutate<Agent>(agentDuplicatePath(sourceId), "POST");
}

export async function updateAgent(id: string, input: UpdateAgentInput): Promise<Agent> {
  return apiMutate<Agent>(agentPath(id), "PATCH", input);
}

export async function deleteAgent(id: string): Promise<void> {
  await apiMutate(agentPath(id), "DELETE");
}

export async function enableAgentToolkit(agentId: string, toolkitId: string): Promise<void> {
  await apiMutate(agentToolkitsPath(agentId), "POST", { toolkit_id: toolkitId });
}

export async function disableAgentToolkit(agentId: string, toolkitId: string): Promise<void> {
  await apiMutate(agentToolkitPath(agentId, toolkitId), "DELETE", undefined, { expects204: true });
}

export async function enableAgentConnector(agentId: string, connectorId: string): Promise<void> {
  await apiMutate(agentConnectorsPath(agentId), "POST", { connector_id: connectorId });
}

export async function disableAgentConnector(agentId: string, connectorId: string): Promise<void> {
  await apiMutate(agentConnectorPath(agentId, connectorId), "DELETE", undefined, {
    expects204: true,
  });
}

/** Share an agent with a principal at the given level. Idempotent: re-sharing an
 * existing `(principal, level)` succeeds. */
export async function shareAgent(
  agentId: string,
  principal: string,
  level: AgentAccessLevel
): Promise<AgentCollaborator> {
  return apiMutate<AgentCollaborator>(agentSharesPath(agentId), "POST", { principal, level });
}

/** Unshare an agent from a principal. Idempotent: unsharing an absent share
 * still succeeds. */
export async function unshareAgent(
  agentId: string,
  principal: string,
  level: AgentAccessLevel
): Promise<void> {
  await apiMutate(agentSharesPath(agentId), "DELETE", { principal, level }, { expects204: true });
}

/** Give up ownership of an agent, removing the caller's own access. The backend
 * returns 204; a 409 means the caller is the agent's only owner (or ownership is
 * group-inherited). */
export async function leaveAgent(agentId: string): Promise<void> {
  await apiMutate(agentLeavePath(agentId), "POST", undefined, { expects204: true });
}
